import { useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';

/** Server-side definition: src/services/brandDashboardMetrics.ts `storeSyncHealth`. */
export type StoreSyncHealth = 'setup_incomplete' | 'never_synced' | 'stale' | 'syncing';

export interface StoreSync {
  id: string;
  storeId?: string;
  storeName?: string;
  status?: string;
  platforms?: string[];
  brandSelected?: boolean;
  authorizationStatus?: 'unverified' | 'pending' | 'verified' | 'rejected' | 'expired' | 'none';
  integrationConnected?: boolean;
  productsSelected?: boolean;
  lastSyncAt?: string | null;
  includedProductCount?: number;
  excludedProductCount?: number;
  countsArePartial?: boolean;
  syncHealth?: StoreSyncHealth;
  website?: string;
}

export interface VendorMetricsSummary {
  vendorId: string;
  vendorName: string;
  /** Product records ingested for the brand. */
  totalProducts: number;
  /** The catalog stores pick from — the denominator for every included/excluded count. */
  catalogProducts?: number;
  /** ProMaster only: multi-brand /warehouse-products catalog. */
  warehouseProducts?: number;
  /** Every store listed on the dashboard. */
  storesCarrying?: number;
  /** Stores that finished setup and synced within `syncStaleAfterDays`. */
  storesSyncing: number;
  storesStale?: number;
  storesNeverSynced?: number;
  storesSettingUp?: number;
  syncStaleAfterDays?: number;
  /** Distinct catalog products at least one store includes. */
  includedProducts: number;
  /** Catalog products no store includes. */
  excludedProducts: number;
  storeSyncs: StoreSync[];
}

export interface VendorConnector {
  id: string;
  vendorId?: string;
  connectorName?: string;
  connectorType?: string;
  status?: string;
  dataDomains?: string[];
  schedule?: {
    frequency?: string;
    lastRun?: string;
    nextRun?: string;
    lastRunStatus?: string;
  };
  handler?: { endpoint?: string };
}

export interface IngestionJobRow {
  id: string;
  status: string;
  type?: string;
  productsProcessed?: number;
  productsCreated?: number;
  productsUpdated?: number;
  errors?: number;
  duration?: number;
  createdAt?: string | null;
  completedAt?: string | null;
}

export interface IngestionSummary {
  totalJobs: number;
  successRate: number;
  avgSyncTime: number;
  lastSyncAt: string | null;
  syncFrequency: string;
}

export interface MapViolation {
  id: string;
  retailerId: string;
  retailerName: string;
  productModel: string;
  status: 'active' | 'resolved' | 'disputed' | 'ignored';
  violationPercentage: number;
  detectedAt: string;
}

export interface EmailIngestionRecord {
  id: string;
  from: string;
  subject: string;
  receivedAt: string;
  recordsParsed: number;
  recordsSaved: number;
  status: 'success' | 'partial' | 'error';
  errors?: string[];
}

/** 401/403/404 mean "nothing to show yet" for a brand-new or unlinked vendor — everything else (5xx, etc.) is a real failure and must surface as one. */
const isExpectedEmptyStatus = (status: number): boolean => status === 401 || status === 403 || status === 404;

export function useVendorMetricsSummary(brandKey: string, enabled: boolean) {
  return useQuery<VendorMetricsSummary | null>({
    queryKey: ['vendor-metrics-summary', brandKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor-metrics/summary', { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return null;
        throw new Error(`Failed to load vendor metrics (${response.status})`);
      }
      const body = await response.json();
      return body?.data || null;
    },
    enabled,
  });
}

export function useVendorConnectors(brandKey: string, enabled: boolean) {
  return useQuery<VendorConnector[]>({
    queryKey: ['vendor-connectors', brandKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor-metrics/connectors', { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return [];
        throw new Error(`Failed to load ingestion connectors (${response.status})`);
      }
      const body = await response.json();
      return body?.data || [];
    },
    enabled,
  });
}

export function useVendorIngestionHistory(brandKey: string, enabled: boolean) {
  return useQuery<{ jobs: IngestionJobRow[]; summary: IngestionSummary | null }>({
    queryKey: ['vendor-ingestion-history', brandKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor-metrics/ingestion-history?limit=10', { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return { jobs: [], summary: null };
        throw new Error(`Failed to load ingestion history (${response.status})`);
      }
      const body = await response.json();
      return body?.data || { jobs: [], summary: null };
    },
    enabled,
  });
}

export function useMapViolationAlertsEnabled(orgKey: string, enabled: boolean) {
  return useQuery<boolean>({
    // Distinct from BillingManagementPage's `['my-subscription', orgKey]` key,
    // which caches the full billing payload (an object) under that same key —
    // sharing it here would let either page read the other's cached shape.
    queryKey: ['map-violation-alerts-enabled', orgKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/billing/my-subscription', { headers });
      if (!response.ok) return false;
      const body = await response.json();
      return Boolean(body?.data?.plan?.limits?.mapViolationAlerts);
    },
    enabled,
  });
}

export function useVendorMapViolations(vendorId: string | undefined, enabled: boolean) {
  return useQuery<MapViolation[]>({
    queryKey: ['vendor-map-violations', vendorId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/map-violations/vendor/${encodeURIComponent(vendorId!)}`, {
        headers,
      });
      if (!response.ok) {
        // The plan gate already runs in useMapViolationAlertsEnabled before this
        // query is even enabled, so a 403 here is the ownership check, not the
        // plan gate — still an "omit, don't error" case (a mismatched vendorId
        // should never happen from this hook's own call site, but isn't worth
        // surfacing as a page-level failure if it does). 401/404 are likewise
        // "nothing to show yet". Anything else is a real failure.
        if (isExpectedEmptyStatus(response.status)) return [];
        throw new Error(`Failed to load MAP violations (${response.status})`);
      }
      const body = await response.json();
      return body?.data || [];
    },
    enabled: enabled && !!vendorId,
  });
}

export function useBrandRebateHistory(orgKey: string, limit = 20, enabled: boolean) {
  return useQuery<EmailIngestionRecord[]>({
    // orgKey keyed: this endpoint is org-scoped server-side, so without it
    // switching impersonation from one vendor org to another would keep
    // serving the previous org's cached rows until an unrelated refetch.
    //
    // Reads /vendor-metrics/rebate-files rather than /email-map/history —
    // the latter only covers email-ingested files (`emailMapIngestions`),
    // missing files this vendor submitted by upload or that an admin
    // ingested on their behalf.
    queryKey: ['vendor-rebate-files', orgKey, limit],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/vendor-metrics/rebate-files?limit=${limit}`, { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return [];
        throw new Error(`Failed to load rebate history (${response.status})`);
      }
      const body = await response.json();
      return body?.data || [];
    },
    enabled,
  });
}

export interface ManagedBrandSummary {
  brand: string;
  fileCount: number;
  lastSubmittedAt: string | null;
}

export function useVendorManagedBrands(orgKey: string, enabled: boolean) {
  return useQuery<ManagedBrandSummary[]>({
    queryKey: ['vendor-managed-brands', orgKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor-metrics/managed-brands', { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return [];
        throw new Error(`Failed to load managed brands (${response.status})`);
      }
      const body = await response.json();
      return body?.data || [];
    },
    enabled,
  });
}
